import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label } from 'reactstrap';
import { useTranslation } from 'react-i18next';

const DateTimePicker = ({ value, onChange, className }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [tempDate, setTempDate] = useState(new Date());
    const [tempTime, setTempTime] = useState("12:00");

    useEffect(() => {
        if (value) {
            const dt = new Date(value);
            if (!isNaN(dt.getTime())) {
                setTempDate(dt);
                setTempTime(value.substring(11, 16));
            }
        }
    }, [value, isOpen]);

    const toggle = () => setIsOpen(!isOpen);

    const handleAccept = () => {
        const year = tempDate.getFullYear();
        const month = String(tempDate.getMonth() + 1).padStart(2, '0');
        const day = String(tempDate.getDate()).padStart(2, '0');
        const newValue = `${year}-${month}-${day}T${tempTime}`;
        onChange({ target: { value: newValue } });
        toggle();
    };

    const handleClear = () => {
        onChange({ target: { value: '' } });
        toggle();
    };

    const displayValue = value ? new Date(value).toLocaleString([], { 
        year: 'numeric', month: '2-digit', day: '2-digit', 
        hour: '2-digit', minute: '2-digit' 
    }) : t('not_set');

    return (
        <div className={className}>
            <div 
                className="d-flex align-items-center justify-content-between p-2 border-custom bg-custom rounded-3 cursor-pointer shadow-sm"
                onClick={toggle}
                style={{ minHeight: '45px' }}
            >
                <div className="ms-2">
                    <span className="me-2">📅</span>
                    <span className="fw-medium">{displayValue}</span>
                </div>
                <Button color="link" size="sm" className="text-decoration-none p-0 me-2">
                    {t('change')}
                </Button>
            </div>

            <Modal isOpen={isOpen} toggle={toggle} centered contentClassName="bg-card-custom text-theme border-custom shadow-lg">
                <ModalHeader toggle={toggle} className="border-0 pb-0 bg-card-custom text-theme">
                    <span className="fw-bold">{t('select_date_time')}</span>
                </ModalHeader>
                <ModalBody className="bg-card-custom">
                    <div className="calendar-wrapper mb-3 d-flex justify-content-center">
                        <Calendar
                            onChange={setTempDate}
                            value={tempDate}
                            className="border-0 rounded-3 shadow-sm"
                        />
                    </div>
                    <div className="mt-3 px-3">
                        <Label className="small fw-bold opacity-50 text-uppercase mb-2">{t('time')}</Label>
                        <Input 
                            type="time" 
                            value={tempTime} 
                            onChange={e => setTempTime(e.target.value)}
                            className="form-control-lg border-custom bg-custom rounded-3"
                        />
                    </div>
                </ModalBody>
                <ModalFooter className="border-0 pt-0 bg-card-custom">
                    <Button color="secondary" outline onClick={handleClear} className="rounded-pill px-4 me-auto">
                        {t('clear')}
                    </Button>
                    <Button color="light" onClick={toggle} className="rounded-pill px-4 border">
                        {t('cancel')}
                    </Button>
                    <Button color="primary" onClick={handleAccept} className="rounded-pill px-4 shadow-sm fw-bold">
                        {t('accept')}
                    </Button>
                </ModalFooter>

                <style>{`
                    .react-calendar {
                        background-color: var(--sb-card-bg);
                        color: var(--sb-text);
                        border-radius: 12px;
                        font-family: inherit;
                        width: 100%;
                    }
                    .react-calendar__navigation button {
                        color: var(--sb-text);
                        font-weight: bold;
                    }
                    .react-calendar__navigation button:enabled:hover,
                    .react-calendar__navigation button:enabled:focus {
                        background-color: var(--sb-bg);
                    }
                    .react-calendar__tile {
                        color: var(--sb-text);
                        border-radius: 8px;
                    }
                    .react-calendar__tile:enabled:hover,
                    .react-calendar__tile:enabled:focus {
                        background-color: var(--sb-bg);
                    }
                    .react-calendar__tile--now {
                        background: var(--sb-accent);
                        color: white;
                    }
                    .react-calendar__tile--active {
                        background: var(--sb-primary)!important;
                        color: white !important;
                    }
                    .react-calendar__month-view__days__day--neighboringMonth {
                        opacity: 0.3;
                    }
                    .cursor-pointer { cursor: pointer; }
                `}</style>
            </Modal>
        </div>
    );
};

export default DateTimePicker;
